import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import * as agentApi from '@/lib/agents/api';
import { getStoredToken } from '@/lib/auth-client';
import type { Agent, AgentEvent } from '@/lib/agents/types';
import { AGENT_TYPE_INFO } from '@/lib/agents/types';
import {
  ArrowLeft,
  Send,
  Loader2,
  Bot,
  User,
  Wrench,
  AlertCircle,
  RefreshCw,
  MoreVertical,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolName?: string;
  toolResult?: unknown;
  isStreaming?: boolean;
  error?: string;
}

export const Route = createFileRoute('/_dashboard/agents/$id/chat')({
  component: AgentChatPage,
});

function AgentChatPage() {
  const navigate = useNavigate();
  const { id: agentId } = Route.useParams();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchAgent = useCallback(async () => {
    try {
      const response = await agentApi.getAgent(agentId);
      setAgent(response.data);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch agent');
      navigate({ to: '/agents' });
    } finally {
      setLoading(false);
    }
  }, [agentId, navigate]);

  useEffect(() => {
    fetchAgent();
    // Generate session ID on mount
    setSessionId(`sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
  }, [fetchAgent]);

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setIsStreaming(true);

    // Add placeholder for assistant response
    setMessages((prev) => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

    try {
      const token = getStoredToken();
      const response = await fetch(`/api/v1/agents/${agentId}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: userMessage, sessionId }),
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const event = JSON.parse(data) as AgentEvent;

            switch (event.type) {
              case 'thinking':
                break;

              case 'token':
                fullContent += event.content || '';
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === 'assistant') {
                    last.content = fullContent;
                  }
                  return updated;
                });
                break;

              case 'tool_start':
                setMessages((prev) => [
                  ...prev,
                  {
                    role: 'tool',
                    content: `Calling ${event.toolName}...`,
                    toolName: event.toolName,
                  },
                ]);
                break;

              case 'tool_end':
                setMessages((prev) => {
                  const updated = [...prev];
                  const toolIdx = updated.findIndex(
                    (m) => m.role === 'tool' && m.toolName === event.toolName && !m.toolResult
                  );
                  if (toolIdx >= 0) {
                    updated[toolIdx] = {
                      ...updated[toolIdx],
                      content: event.toolError
                        ? `${event.toolName} failed: ${event.toolError}`
                        : `${event.toolName} completed`,
                      toolResult: event.toolResult,
                      error: event.toolError,
                    };
                  }
                  return updated;
                });
                break;

              case 'done':
                if (event.output) {
                  setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last && last.role === 'assistant') {
                      last.content = event.output!.content;
                      last.isStreaming = false;
                    }
                    return updated;
                  });
                }
                break;

              case 'error':
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === 'assistant') {
                    last.content = event.error || 'An error occurred';
                    last.isStreaming = false;
                    last.error = event.error;
                  }
                  return updated;
                });
                break;
            }
          } catch (e) {
            console.warn('Failed to parse SSE event:', data);
          }
        }
      }
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to send message';
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === 'assistant') {
          last.content = errorMessage;
          last.isStreaming = false;
          last.error = errorMessage;
        }
        return updated;
      });
      toast.error(errorMessage);
    } finally {
      setIsStreaming(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    if (messages.length === 0) return;
    if (!confirm('Clear chat history?')) return;

    setMessages([]);
    setSessionId(`sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
    toast.success('Chat cleared');
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-4 p-4 border-b">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex-1 p-4">
          <Skeleton className="h-full" />
        </div>
      </div>
    );
  }

  if (!agent) return null;

  const typeInfo = AGENT_TYPE_INFO[agent.agent_type];

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/agents/${agentId}` as any}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            <h1 className="font-semibold">{agent.name}</h1>
            <Badge variant="outline" className="text-xs">
              {typeInfo.label}
            </Badge>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleClearChat}>
              <Trash2 className="mr-2 h-4 w-4" />
              Clear Chat
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/agents/${agentId}` as any}>
                <RefreshCw className="mr-2 h-4 w-4" />
                New Session
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Start a conversation with {agent.name}</p>
              {agent.description && <p className="text-sm mt-1">{agent.description}</p>}
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role !== 'user' && (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {message.role === 'tool' ? (
                    <Wrench className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </div>
              )}

              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : message.role === 'tool'
                      ? 'bg-muted border'
                      : message.error
                        ? 'bg-destructive/10 border border-destructive/30'
                        : 'bg-muted'
                }`}
              >
                {message.error && (
                  <div className="flex items-center gap-2 text-destructive mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Error</span>
                  </div>
                )}

                <p className="whitespace-pre-wrap">{message.content}</p>

                {message.isStreaming && (
                  <span className="inline-block w-2 h-4 bg-current animate-pulse ml-1" />
                )}

                {message.role === 'tool' && message.toolResult != null && (
                  <pre className="mt-2 text-xs bg-background/50 p-2 rounded overflow-x-auto">
                    {JSON.stringify(message.toolResult, null, 2).slice(0, 200)}
                    {JSON.stringify(message.toolResult).length > 200 ? '...' : null}
                  </pre>
                )}
              </div>

              {message.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t bg-background">
        <div className="max-w-3xl mx-auto flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${agent.name}...`}
            disabled={isStreaming}
            className="flex-1"
          />
          <Button onClick={handleSend} disabled={!input.trim() || isStreaming}>
            {isStreaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
